/**
 * The agent's experiments: one score column per evaluator (the union across
 * experiments, since each may pick different ones), filtered by dataset, with
 * the score-over-time chart above.
 */

import { useState } from "react";
import { Link } from "react-router";
import type { UseQueryResult } from "@tanstack/react-query";
import { Box } from "@/components/ui/box";
import { ButtonLink } from "@/components/ui/button";
import { EmptyState, QueryState } from "@/components/ui/states";
import { ExperimentStatusPill } from "@/features/experiments/status-pill";
import type { ExperimentSummary } from "@/features/experiments/types";
import { formatDate, formatMoney, formatScore } from "@/lib/format";
import { CompareButton, SelectCell, useCompareSelection } from "@/features/compare/use-compare-selection";
import { ScoreChart } from "./score-chart";
import { Select } from "@/components/ui/select";
import { datasetLabel } from "@/features/experiments/dataset-label";

function scoreOf(e: ExperimentSummary, key: string): number | null {
  return e.aggregate?.evaluators[key]?.mean ?? e.live?.[key]?.mean ?? null;
}

export function AgentExperiments({
  agentId,
  experiments,
}: {
  agentId: string;
  experiments: UseQueryResult<ExperimentSummary[]>;
}) {
  const [dataset, setDataset] = useState("all");
  const all = experiments.data ?? [];
  const datasets = [
    ...new Map(all.flatMap((e) => (e.dataset ? [[e.dataset.id, e.dataset.name] as const] : []))).entries(),
  ];
  const rows = dataset === "all" ? all : all.filter((e) => e.dataset?.id === dataset);
  const evaluators = [...new Map(rows.flatMap((e) => e.evaluators).map((v) => [v.key, v.name])).entries()];
  const names = Object.fromEntries(evaluators);
  const { selection, picked } = useCompareSelection(all);

  return (
    <>
      <ScoreChart agentId={agentId} names={names} experiments={all} />
      <div className="flex items-center gap-2">
        {datasets.length > 1 && (
          <>
            <label htmlFor="agent-ds" className="text-[13px] text-muted">
              Dataset
            </label>
            <Select
              id="agent-ds"
              value={dataset}
              onChange={setDataset}
              options={[
                { value: "all", label: "All datasets" },
                ...datasets.map(([id, name]) => ({ value: id, label: name })),
              ]}
            />
          </>
        )}
        <span className="grow" />
        {all.length > 1 && <CompareButton picked={picked} />}
      </div>
      <Box>
        <QueryState query={experiments} what="experiments">
          {rows.length === 0 ? (
            <EmptyState
              title="No experiments yet"
              action={
                <ButtonLink variant="primary" to={`/experiments/new?agent=${agentId}`}>
                  New experiment
                </ButtonLink>
              }
            >
              An experiment runs this agent on every item of a dataset and scores each answer.
            </EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>
                      <span className="sr-only">Select</span>
                    </th>
                    <th>Experiment</th>
                    <th>Dataset</th>
                    <th className="num">Overall</th>
                    {evaluators.map(([key, name]) => (
                      <th key={key} className="num">
                        {name}
                      </th>
                    ))}
                    <th className="num">Cost</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id} data-selected={selection.selected.has(e.id)}>
                      <SelectCell experiment={e} selection={selection} />
                      <td>
                        <Link to={`/experiments/${e.id}`} className="font-semibold hover:underline">
                          {e.name}
                        </Link>
                      </td>
                      <td className="text-muted">{e.dataset ? datasetLabel(e.dataset) : "—"}</td>
                      <td className="num font-semibold">{formatScore(e.aggregate?.overall)}</td>
                      {evaluators.map(([key]) => (
                        <td key={key} className="num">
                          {formatScore(scoreOf(e, key))}
                        </td>
                      ))}
                      <td className="num text-muted">
                        {e.status === "draft" ? "—" : formatMoney(e.spend.total_usd)}
                      </td>
                      <td>
                        <ExperimentStatusPill experiment={e} />
                      </td>
                      <td className="text-xs text-muted">
                        {e.created_by}
                        <br />
                        {formatDate(e.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryState>
      </Box>
    </>
  );
}
